import { Archive, FileText, Plus, Sparkles } from "lucide-react";
import * as React from "react";

import { type FormDef, formsApi } from "@/api/forms";
import { AIFormSuggesterDialog } from "@/components/admin/AIFormSuggesterDialog";
import { EmptyForms } from "@/components/illustrations/EmptyForms";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { LoadingCards } from "@/components/ui/loading-cards";
import { useRouteStore } from "@/stores/route";
import { useUIStore } from "@/stores/ui";

export function FormsPage(): JSX.Element {
  const [forms, setForms] = React.useState<FormDef[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [creating, setCreating] = React.useState(false);
  const [createCode, setCreateCode] = React.useState("");
  const [createName, setCreateName] = React.useState("");
  const [showArchived, setShowArchived] = React.useState(false);
  const [aiOpen, setAiOpen] = React.useState(false);
  const navigate = useRouteStore((s) => s.navigate);
  const pushToast = useUIStore((s) => s.pushToast);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const list = await formsApi.list(showArchived);
      setForms(list);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lỗi tải danh sách",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [showArchived, pushToast]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleCreate = async (): Promise<void> => {
    if (!createCode || !createName) {
      pushToast({ variant: "warning", title: "Cần code + name" });
      return;
    }
    if (!/^[a-z][a-z0-9_]{1,63}$/.test(createCode)) {
      pushToast({
        variant: "warning",
        title: "Code phải snake_case",
        description: "VD: pr_standard, sale_quotation_full",
      });
      return;
    }
    try {
      // Tạo schema rỗng — user sẽ thêm field trong builder
      await formsApi.create({
        code: createCode,
        name: createName,
        schema: {
          code: createCode,
          title: createName,
          fields: [
            {
              name: "placeholder_field",
              type: "text",
              label: "Field đầu tiên (xoá khi build xong)",
            },
          ],
        },
      });
      pushToast({ variant: "success", title: `Tạo form ${createCode}` });
      setCreating(false);
      setCreateCode("");
      setCreateName("");
      // Mở thẳng builder
      navigate("admin.forms.edit", createCode);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Tạo thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const handleArchive = async (code: string): Promise<void> => {
    if (!confirm(`Archive form ${code}? Người dùng không gọi được nữa.`)) return;
    try {
      await formsApi.archive(code);
      pushToast({ variant: "success", title: `Đã archive ${code}` });
      await refresh();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Archive thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  return (
    <div className="flex-1 flex flex-col">
      <header className="h-12 shrink-0 flex items-center px-6 gap-3 border-b bg-background/50">
        <h1 className="font-semibold">Form Builder</h1>
        <div className="ml-auto flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Archived
          </label>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAiOpen(true)}
            title="AI gen form từ mô tả"
          >
            <Sparkles className="size-4 mr-1" /> AI suggest
          </Button>
          <Button size="sm" variant="brand" onClick={() => setCreating(true)}>
            <Plus className="size-4 mr-1" /> Tạo form mới
          </Button>
        </div>
      </header>
      <AIFormSuggesterDialog
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        onCreated={refresh}
      />

      <main className="flex-1 max-w-5xl w-full mx-auto p-6 space-y-4 overflow-y-auto">
        {creating ? (
          <Card>
            <CardContent className="p-4 space-y-3">
              <h3 className="font-medium">Tạo form mới</h3>
              <div>
                <label className="text-xs font-medium block mb-1">
                  Code (snake_case)
                </label>
                <Input
                  value={createCode}
                  onChange={(e) =>
                    setCreateCode(
                      e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"),
                    )
                  }
                  placeholder="pr_standard"
                />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">
                  Tên hiển thị
                </label>
                <Input
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="Yêu cầu mua hàng tiêu chuẩn"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setCreating(false)}>
                  Huỷ
                </Button>
                <Button onClick={() => void handleCreate()}>Tạo + Mở Builder</Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {loading ? (
          <LoadingCards count={4} />
        ) : forms.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <EmptyState
                hero={<EmptyForms size={140} />}
                title="Chưa có form nào"
                description="Tạo form đầu tiên để AI có thể thu thập thông tin có cấu trúc từ user."
                action={
                  <Button variant="brand" onClick={() => setCreating(true)}>
                    <Plus className="size-4 mr-1" /> Tạo form đầu tiên
                  </Button>
                }
                hint="💡 Có thể bắt đầu với template PR Standard cho yêu cầu mua hàng."
              />
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {forms.map((f) => (
              <Card key={f.id}>
                <CardContent className="p-4 flex items-center gap-3">
                  <FileText className="size-5 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{f.name}</span>
                      <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                        {f.code}
                      </code>
                      {f.published_version != null ? (
                        <Badge variant="default" className="text-[10px]">
                          v{f.published_version} live
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px]">
                          Chưa publish
                        </Badge>
                      )}
                      {f.is_archived ? (
                        <Badge variant="outline" className="text-[10px]">
                          <Archive className="size-3 mr-1" /> Archived
                        </Badge>
                      ) : null}
                    </div>
                    {f.description ? (
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {f.description}
                      </p>
                    ) : null}
                    <div className="text-[11px] text-muted-foreground mt-1">
                      Current v{f.current_version} · {f.versions.length} version(s)
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm"
                      onClick={() => navigate("admin.forms.edit", f.code)}
                    >
                      Edit
                    </Button>
                    {!f.is_archived ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void handleArchive(f.code)}
                      >
                        <Archive className="size-4" />
                      </Button>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
